import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { GraduationCap, Briefcase } from 'lucide-react';
import { useTypingAnimation } from '@/hooks/use-typing-animation';

const ResumeSection = () => {
  const { ref: headingRef, displayedText } = useTypingAnimation('Education & Experience', 60);
  
  const education = [
    {
      degree: 'Bachelor of Physics',
      institution: 'University',
      period: '2019 - 2025',
      description: 'Comprehensive study of physics principles with focus on analytical thinking and problem-solving skills applicable to data-driven digital marketing strategies.',
    },
    {
      degree: 'Higher Secondary Certificate (Science)',
      institution: 'College',
      period: '2017 - 2019',
      description: 'Strong foundation in scientific methodology and analytical reasoning.',
    },
  ];

  const experience = [
    {
      position: 'Senior SEO Specialist & Digital Marketing Consultant',
      company: 'Digital Growth Agency',
      period: '2020 - Present',
      description:
        'Leading comprehensive SEO strategies and digital marketing campaigns. Specializing in technical SEO, local SEO optimization, YouTube SEO, and Meta Ads management with proven track record of 250% ranking improvements.',
    },
    {
      position: 'SEO Strategist & Content Optimizer',
      company: 'Marketing Solutions Pro',
      period: '2018 - 2020',
      description:
        'Developed and executed data-driven SEO strategies for diverse clients. Managed local SEO campaigns, implemented technical SEO improvements, and optimized content for search engines resulting in significant organic traffic growth.',
    },
    {
      position: 'Digital Marketing Specialist',
      company: 'E-Commerce Hub',
      period: '2016 - 2018',
      description:
        'Implemented SEO best practices and managed paid advertising campaigns. Conducted keyword research, on-page optimization, and competitor analysis to improve search visibility and drive business growth.',
    },
  ];

  return (
    <section id="resume" className="py-24">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16 animate-fade-in">
          <p className="text-primary font-semibold mb-2 uppercase tracking-wider text-sm">Resume</p>
          <h2 ref={headingRef as any} className="text-4xl md:text-5xl font-bold mb-4 min-h-[3rem]">
            {displayedText}
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            My professional journey showcasing academic achievements and career milestones.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Education */}
          <div className="space-y-6 animate-fade-in">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-2xl font-bold">Education</h3>
            </div>
            {education.map((edu, index) => (
              <Card
                key={index}
                className="card-hover border-l-4 border-l-primary"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <CardHeader>
                  <CardTitle className="text-xl">{edu.degree}</CardTitle>
                  <div className="flex justify-between items-center text-sm text-muted-foreground">
                    <span>{edu.institution}</span>
                    <span className="font-semibold">{edu.period}</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{edu.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Experience */}
          <div className="space-y-6 animate-fade-in-up">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Briefcase className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-2xl font-bold">Experience</h3>
            </div>
            {experience.map((exp, index) => (
              <Card
                key={index}
                className="card-hover border-l-4 border-l-primary"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <CardHeader>
                  <CardTitle className="text-xl">{exp.position}</CardTitle>
                  <div className="flex justify-between items-center text-sm text-muted-foreground">
                    <span>{exp.company}</span>
                    <span className="font-semibold">{exp.period}</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{exp.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ResumeSection;
